import { useState } from 'react';
import { HelpCircle, FileText, Camera, Share2, CreditCard, LayoutDashboard, TrendingUp, ChevronRight, ChevronLeft, X, Sparkles } from 'lucide-react';
import { T } from '../theme';

const SECTIONS = [
  {
    icon: LayoutDashboard,
    color: T.tealDeep,
    bg: '#E6F6F7',
    title: 'The Dashboard',
    steps: [
      'The dashboard is your home base. From here, you can quickly add new expenses to your active report, view your recent expenses, and access pre-configured analysis and views into your finances and expensereports.',
      'Quick Actions let you scan a receipt, add an expense manually, or start a new expense report — all in one tap.',
      'The Finances section gives you a Financial Summary (income vs. expenses), a Tax Summary (IRS deduction categories), and quick access to add a new bank or credit card account.',
      'Preset Views are shortcuts to filtered transaction views — Business P&L, Personal Expenses, Needs Review, and more. Tap any card to jump straight there.',
    ],
  },
  {
    icon: Sparkles,
    color: T.tealDeep,
    bg: '#E6F6F7',
    title: 'Talk to MySpend (AI)',
    steps: [
      'Talk to MySpend is your AI bookkeeper. Tap the sparkle icon (✦) in the sidebar or header to open a chat panel. Ask questions about your finances in plain English — no special syntax needed.',
      'You can ask anything about your data: "What are my top business expenses this month?", "Show me unclassified transactions", "How much did I spend on travel in Q1?", or "What\'s my net business income year to date?"',
      'The AI looks up your actual expense reports and bank transactions to answer — it does not guess. It can search across multiple reports, compare categories, find specific vendors, and cross-reference expenses against bank data.',
      'Type / to use commands: /clear resets the chat, /condense summarises older turns to free up context, /show_summary shows the last summary with token counts, /usage shows your token and query stats, and /samples shows example questions.',
      'Use the ↑ and ↓ arrow keys in the input box to cycle back through your previous questions and re-send or edit them.',
      'Each AI response has two copy buttons. "Copy" gives you clean plain text — perfect for pasting into emails or docs. The smaller "MD" button copies the raw Markdown if you need the formatting preserved.',
    ],
  },
  {
    icon: FileText,
    color: '#15803d',
    bg: '#F0FDF4',
    title: 'Expense Reports',
    steps: [
      'Tap "New Report" on the dashboard to create an expense report. Give it a name — e.g. "June 2025 Travel" — and it becomes your active report.',
      'To switch reports later, tap the report name in the top bar (it looks like a dropdown). All your saved reports are listed there.',
      'Each report stores its own list of expenses. You can have as many reports as your plan allows.',
      'Your last-opened report is remembered across sessions, so you pick up right where you left off.',
    ],
  },
  {
    icon: Camera,
    color: T.tealDeep,
    bg: '#E6F6F7',
    title: 'Adding Expenses & Scanning',
    steps: [
      'Tap "Add Expense" to enter an expense manually. Fill in vendor, amount, date, and category. Tap Save.',
      'Tap "Scan Receipt" to photograph or upload a receipt. AI extracts the vendor, total, tax, and date automatically — review and save.',
      'After scanning you can crop the image before confirming. The receipt image is stored with the expense for reference.',
      'Expenses are auto-saved to the cloud every 1.5 seconds — no manual save needed after editing.',
      'You can also email receipts directly to receipts@myspend.info from your account email — on Starter and Pro plans. Attachments (images or PDFs) are scanned automatically; plain-text emails work too. Items land in an "Emailed Receipts" report for you to review.',
    ],
  },
  {
    icon: Share2,
    color: '#9333ea',
    bg: '#FAF5FF',
    title: 'Exporting, Live Links & Sharing',
    steps: [
      'To export, open an expense report and tap the export icon. You can download a CSV or PDF of all expenses.',
      'Live Review Links let an external reviewer (no account needed) view your report and leave comments. Tap the link icon in the top bar to generate one. Optionally set a password.',
      'To share a report with another MySpend user, tap the share icon in the top bar and enter their email address. They can view or edit depending on the access level you grant.',
      'Shared reports and active review links appear under the Load screen so you can manage them any time.',
    ],
  },
  {
    icon: CreditCard,
    color: '#0891b2',
    bg: '#ECFEFF',
    title: 'Importing Bank & CC Transactions',
    steps: [
      'Download a CSV export from your bank or credit card website, then tap the import icon (or use the Sidebar menu) to import it.',
      'Imported transactions are grouped into accounts. Multiple statement files for the same account (e.g. Chase Jan, Chase Feb) are automatically grouped together.',
      'Once imported, open the Financial view to classify each transaction: Business Expense, Personal, Income, Transfer, etc. Classifications are saved automatically.',
      'Unclassified transactions appear in the "Needs Review" preset view. Work through them to keep your books current.',
      'Transaction data can optionally be kept local (device only) — it never leaves your browser. Toggle "Keep local" when importing.',
    ],
  },
  {
    icon: LayoutDashboard,
    color: T.amberDeep,
    bg: '#FFFBEB',
    title: 'Preset Views',
    steps: [
      'Preset Views are curated filtered views of your transaction data, accessible from the dashboard in one tap.',
      'Business P&L shows only business income and expenses. Personal P&L does the same for personal finances.',
      '"By Classification" groups everything by how you\'ve tagged it. "By Category" groups by spending category.',
      '"Needs Review" filters to unclassified transactions — your to-do list for keeping books current.',
      // 'The stat strip at the top of the dashboard updates as you classify transactions, so you can track progress at a glance.',
    ],
  },
  {
    icon: TrendingUp,
    color: '#1e3a5f',
    bg: '#EFF6FF',
    title: 'Financial Summary & Taxes',
    steps: [
      'Tap "Financial Summary" on the dashboard to see income vs. expenses broken down by classification and date range.',
      'Use the date range selector to filter to a specific month, quarter, or year. YTD is the default.',
      'Tap "Tax Summary" for an IRS-ready breakdown. Expenses are mapped to Schedule C deduction categories automatically.',
      'Each expense category maps to an IRS category (e.g. Meals → Meals & Entertainment, Travel → Travel). You can override the IRS category on individual expenses in the "Other" category.',
      'When you\'re ready for taxes, export the Tax Summary as a CSV and hand it to your accountant, or reference the totals yourself.',
    ],
  },
];

export default function HelpModal({ onClose }) {
  const [sectionIdx, setSectionIdx] = useState(0);
  const [stepIdx, setStepIdx] = useState(0);
  const section = SECTIONS[sectionIdx];
  const Icon = section.icon;
  const isLastStep = stepIdx === section.steps.length - 1;
  const isLastSection = sectionIdx === SECTIONS.length - 1;

  const next = () => {
    if (!isLastStep) { setStepIdx(s => s + 1); return; }
    if (!isLastSection) { setSectionIdx(s => s + 1); setStepIdx(0); }
  };
  const prev = () => {
    if (stepIdx > 0) { setStepIdx(s => s - 1); return; }
    if (sectionIdx > 0) { setSectionIdx(s => s - 1); setStepIdx(SECTIONS[sectionIdx - 1].steps.length - 1); }
  };
  const hasPrev = sectionIdx > 0 || stepIdx > 0;
  const isDone = isLastStep && isLastSection;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        zIndex: 1100, backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px 28px',
          width: '100%', maxWidth: 420, animation: 'slideUp 0.3s ease-out',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HelpCircle size={18} color={T.tealDeep} />
            <span style={{ fontSize: 13, fontWeight: 800, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase' }}>Help</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 4, display: 'flex' }}>
            <X size={20} />
          </button>
        </div>

        {/* Section tabs */}
        <div style={{ display: 'flex', gap: 5, overflowX: 'auto', marginBottom: 20, paddingBottom: 2 }}>
          {SECTIONS.map((s, i) => {
            const SIcon = s.icon;
            const active = i === sectionIdx;
            return (
              <button
                key={i}
                onClick={() => { setSectionIdx(i); setStepIdx(0); }}
                style={{
                  flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5,
                  padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                  border: '1.5px solid', cursor: 'pointer',
                  borderColor: active ? s.color : '#E5E7EB',
                  background: active ? s.bg : 'transparent',
                  color: active ? s.color : '#9CA3AF',
                }}
              >
                <SIcon size={12} />
                {s.title}
              </button>
            );
          })}
        </div>

        {/* Section icon + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: section.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon size={22} color={section.color} />
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#111', lineHeight: 1.2 }}>{section.title}</div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
              Step {stepIdx + 1} of {section.steps.length}
            </div>
          </div>
        </div>

        {/* Step content */}
        <div style={{
          background: '#F9FAFB', borderRadius: 12, padding: '16px',
          fontSize: 14, color: '#374151', lineHeight: 1.6, minHeight: 90,
        }}>
          {section.steps[stepIdx]}
        </div>

        {/* Step dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 14 }}>
          {section.steps.map((_, i) => (
            <button
              key={i}
              onClick={() => setStepIdx(i)}
              style={{
                width: i === stepIdx ? 18 : 6, height: 6, borderRadius: 3,
                background: i === stepIdx ? section.color : '#D1D5DB',
                border: 'none', cursor: 'pointer', padding: 0,
                transition: 'all 0.2s',
              }}
            />
          ))}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          {hasPrev && (
            <button
              onClick={prev}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '11px 16px', borderRadius: 10, border: '1.5px solid #E5E7EB',
                background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#6B7280',
              }}
            >
              <ChevronLeft size={16} /> Back
            </button>
          )}
          <button
            onClick={isDone ? onClose : next}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              padding: '11px 16px', borderRadius: 10, border: 'none',
              background: isDone ? T.gradTeal : section.color,
              fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#fff',
            }}
          >
            {isDone ? 'Done' : (<>Next <ChevronRight size={16} /></>)}
          </button>
        </div>

        {/* Full docs link */}
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <a
            href="/help/"
            style={{
              fontSize: 12, color: '#9CA3AF', textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontWeight: 600,
            }}
          >
            View full help documentation
          </a>
        </div>
      </div>
    </div>
  );
}
